import { Button, Modal, Textarea } from '@mantine/core';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ReauthField, useReauth } from '../../approval-policy.js';
import classes from './NoonReportScreen.module.css';

/**
 * Gönderilmiş raporu yeniden açma (ADR 0022 §3): gerekçe ve açık onay adımı (düğme). Parola alanı
 * yalnız şirket ayarı açıkken ya da sunucu isterse görünür (ADR 0075); onay olmadığı için
 * parolasız pencerede "onaylıyorsunuz" bilgi metni yazılmaz. Noon ve olay raporları ortak kullanır.
 */
export function ReopenDialog({
  opened,
  onClose,
  onConfirm,
}: {
  opened: boolean;
  onClose: () => void;
  /** Parola yalnız girildiyse gelir. */
  onConfirm: (reason: string, password: string | undefined) => Promise<string | null>;
}) {
  const { t } = useTranslation();
  const k = (key: string) => t(`voyage.noon.reopenDialog.${key}`);
  const [reason, setReason] = useState('');
  const reauth = useReauth('voyage_report');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const close = () => {
    setReason('');
    reauth.reset();
    setError(null);
    onClose();
  };
  return (
    <Modal
      opened={opened}
      onClose={close}
      title={k('title')}
      closeButtonProps={{ 'aria-label': k('cancel') }}
    >
      <p className={classes.muted}>{k('body')}</p>
      <Textarea
        label={k('reason')}
        required
        value={reason}
        onChange={(event) => setReason(event.currentTarget.value)}
        maxLength={1000}
        autosize
        minRows={2}
        mb="sm"
      />
      {reauth.shown ? <ReauthField reauth={reauth} label={k('password')} /> : null}
      {error ? (
        <p className={classes.message} data-tone="danger" role="alert">
          {t(`voyage.errors.${error}`, { defaultValue: t('voyage.errors.GENERIC') })}
        </p>
      ) : null}
      <div className={classes.dialogActions}>
        <Button variant="default" onClick={close}>
          {k('cancel')}
        </Button>
        <Button
          loading={busy}
          disabled={reauth.missing}
          onClick={async () => {
            if (reauth.missing) return;
            setBusy(true);
            const code = await onConfirm(reason, reauth.body().password);
            setBusy(false);
            if (code) {
              reauth.noteError({ code });
              setError(code);
            } else {
              setReason('');
              reauth.reset();
              setError(null);
            }
          }}
        >
          {k('confirm')}
        </Button>
      </div>
    </Modal>
  );
}
